import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import "./Filters.css";

const Filters = ({ setFilteredData }) => {
  const [allData, setAllData] = useState([]);
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });

  const [pid, setPid] = useState([]);
  const [orgName, setOrgName] = useState([]);
  const [managerName, setManagerName] = useState([]);

  const [filteredDropdownData, setFilteredDropdownData] = useState({
    pid: [],
    orgName: [],
    managerName: [],
  });

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);

  /* ================= CUSTOM STYLE ================= */

  const multiSelectStyles = {
    container: (provided) => ({
      ...provided,
      width: "230px",
    }),
    control: (provided) => ({
      ...provided,
      minHeight: "45px",
      maxHeight: "120px",
      overflowY: "auto",
    }),
    valueContainer: (provided) => ({
      ...provided,
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      padding: "5px",
    }),
    multiValue: (provided) => ({
      ...provided,
      width: "100%",
      marginBottom: "4px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      fontSize: "13px",
    }),
    menu: (provided) => ({
      ...provided,
      width: "300px",
    }),
  };

  /* ================= INITIAL MASTER DATA FETCH ================= */

  useEffect(() => {
    const fetchAllDropdownData = async () => {
      setInitialLoading(true);
      try {
        const response = await axios.get(
          "https://api-usersreport-insights.haqdarshak.com/dropdown-data-all"
        );

        const data = response.data;
        setAllData(data);

        const uniquePIDs = [
          ...new Set(data.map((item) => item.Organization_PID)),
        ].filter(Boolean);

        const uniqueOrgs = [
          ...new Set(data.map((item) => item.Organization_Project_Name)),
        ].filter(Boolean);

        const uniqueManagers = [
          ...new Set(data.map((item) => item.Manager_Name)),
        ].filter(Boolean);

        setFilteredDropdownData({
          pid: uniquePIDs,
          orgName: uniqueOrgs,
          managerName: uniqueManagers,
        });

        /* ========= RESTORE SAVED FILTERS ========= */

        const restoreFilters = (key, validOptions) => {
          try {
            const raw = localStorage.getItem(key);
            if (!raw) return [];
        
            let arr = [];
        
            // Case 1: stored as JSON array (multi-select screen)
            if (raw.startsWith("[")) {
              const parsed = JSON.parse(raw);
              arr = Array.isArray(parsed) ? parsed : [parsed];
            } 
            // Case 2: stored as normal string (stateview screen)
            else {
              arr = [raw];
            }
        
            return arr.filter((val) => validOptions.includes(val));
          } catch (err) {
            console.error("Restore error:", key, err);
            return [];
          }
        };
        

        const validPid = restoreFilters("selectedPid", uniquePIDs);
        const validOrg = restoreFilters("selectedOrgName", uniqueOrgs);
        const validManager = restoreFilters(
          "selectedManagerName",
          uniqueManagers
        );

        setPid(validPid);
        setOrgName(validOrg);
        setManagerName(validManager);
      } catch (error) {
        console.error("Error fetching dropdown master data:", error);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchAllDropdownData();
  }, []);

  /* ================= DEPENDENT FILTER LOGIC ================= */

  useEffect(() => {
    if (!allData.length) return;

    let pidFiltered = allData;

    if (orgName.length) {
      pidFiltered = pidFiltered.filter((item) =>
        orgName.includes(item.Organization_Project_Name)
      );
    }

    if (managerName.length) {
      pidFiltered = pidFiltered.filter((item) =>
        managerName.includes(item.Manager_Name)
      );
    }

    const uniquePIDs = [
      ...new Set(pidFiltered.map((item) => item.Organization_PID)),
    ].filter(Boolean);

    let orgFiltered = allData;

    if (pid.length) {
      orgFiltered = orgFiltered.filter((item) =>
        pid.includes(item.Organization_PID)
      );
    }

    if (managerName.length) {
      orgFiltered = orgFiltered.filter((item) =>
        managerName.includes(item.Manager_Name)
      );
    }

    const uniqueOrgs = [
      ...new Set(orgFiltered.map((item) => item.Organization_Project_Name)),
    ].filter(Boolean);

    let managerFiltered = allData;

    if (pid.length) {
      managerFiltered = managerFiltered.filter((item) =>
        pid.includes(item.Organization_PID)
      );
    }

    if (orgName.length) {
      managerFiltered = managerFiltered.filter((item) =>
        orgName.includes(item.Organization_Project_Name)
      );
    }

    const uniqueManagers = [
      ...new Set(managerFiltered.map((item) => item.Manager_Name)),
    ].filter(Boolean);

    setFilteredDropdownData({
      pid: uniquePIDs,
      orgName: uniqueOrgs,
      managerName: uniqueManagers,
    });
  }, [pid, orgName, managerName, allData]);

  /* ================= APPLY FILTER ================= */

  const handleFilterSubmit = async () => {
    setLoading(true);

    try {
      localStorage.setItem("selectedPid", JSON.stringify(pid));
      localStorage.setItem("selectedOrgName", JSON.stringify(orgName));
      localStorage.setItem("selectedManagerName", JSON.stringify(managerName));

      const response = await axios.get("https://api-usersreport-insights.haqdarshak.com/users", {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          pid,
          orgName,
          managerName,
        },
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams();
          Object.keys(params).forEach((key) => {
            if (Array.isArray(params[key])) {
              params[key].forEach((val) =>
                searchParams.append(key, val)
              );
            } else if (params[key]) {
              searchParams.append(key, params[key]);
            }
          });
          return searchParams.toString();
        },
      });

      setFilteredData(response.data);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ================= CLEAR FILTER ================= */

  const handleClearFilters = () => {
    localStorage.removeItem("selectedPid");
    localStorage.removeItem("selectedOrgName");
    localStorage.removeItem("selectedManagerName");

    setDateRange({ startDate: "", endDate: "" });
    setPid([]);
    setOrgName([]);
    setManagerName([]);
    setFilteredData([]);
  };

  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split("T")[0];
  };

  const minDate = "2024-04-01";
  const maxDate = getYesterdayDate();

  const toOptions = (arr) =>
    arr.map((val) => ({ label: val, value: val }));

  return (
    <div className="filters-container">
      {initialLoading && (
        <div className="loading-indicator">Loading Filters...</div>
      )}

      {/* DATE FILTER */}
      <div className="filter-group">
        <label>Date Range:</label>
        <div className="date-row">
          <input
            type="date"
            value={dateRange.startDate}
            min={minDate}
            max={maxDate}
            onChange={(e) =>
              setDateRange({ ...dateRange, startDate: e.target.value })
            }
            className="filter-date"
          />

          <span className="date-separator">to</span>

          <input
            type="date"
            value={dateRange.endDate}
            min={minDate}
            max={maxDate}
            onChange={(e) =>
              setDateRange({ ...dateRange, endDate: e.target.value })
            }
            className="filter-date"
          />
        </div>
      </div>

      {/* PID */}
      <div className="filter-group">
        <label>PID:</label>
        <Select
          isMulti
          styles={multiSelectStyles}
          options={toOptions(filteredDropdownData.pid)}
          value={filteredDropdownData.pid
            .filter((option) => pid.includes(option))
            .map((val) => ({ label: val, value: val }))}
          onChange={(selected) =>
            setPid(selected ? selected.map((s) => s.value) : [])
          }
        />
      </div>

      {/* ORG */}
      <div className="filter-group">
        <label>Organization/State Name:</label>
        <Select
          isMulti
          styles={multiSelectStyles}
          options={toOptions(filteredDropdownData.orgName)}
          value={filteredDropdownData.orgName
            .filter((option) => orgName.includes(option))
            .map((val) => ({ label: val, value: val }))}
          onChange={(selected) =>
            setOrgName(selected ? selected.map((s) => s.value) : [])
          }
        />
      </div>

      {/* MANAGER */}
      <div className="filter-group">
        <label>Manager Name:</label>
        <Select
          isMulti
          styles={multiSelectStyles}
          options={toOptions(filteredDropdownData.managerName)}
          value={filteredDropdownData.managerName
            .filter((option) => managerName.includes(option))
            .map((val) => ({ label: val, value: val }))}
          onChange={(selected) =>
            setManagerName(selected ? selected.map((s) => s.value) : [])
          }
        />
      </div>

      {/* BUTTONS */}
      <div className="filter-buttons">
        <button
          className="filter-button"
          onClick={handleFilterSubmit}
          disabled={loading}
        >
          {loading ? "Loading..." : "Apply Filters"}
        </button>

        <button
          className="filter-button clear-button"
          onClick={handleClearFilters}
        >
          Clear Filters
        </button>
      </div>
      {/* NOTES */}
      <p style={{ marginTop: "-20px", fontSize: "14px", color: "gray", marginBottom: "-5px " }}>
        📌 This data is from 1st April 2025
      </p>
      <p style={{ marginTop: "-20px", fontSize: "14px", color: "gray" , marginBottom: "-5px "}}>
        📌 Individual manager-wise data is roll-up data, so summing up the total managers in a project will not equal the project’s total data.
      </p>
    </div>
  );
};

export default Filters;
